"use client";
import { Button } from "@/components/ui/button";
import { CartContext } from "@/context/CartContext";
import { WishlistContext } from "@/context/WishlistContext";
import { categories } from "@/lib/categories";
import { ChevronDown, Heart, ShoppingCart, User } from "lucide-react";
import { useSession } from "next-auth/react";
import Image from "next/image";
import Link from "next/link";
import { useContext, useEffect, useState } from "react";
import ProfileMenu from "./ProfileMenu";
import Search from "./Search";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";

const Navbar = () => {
  const { cart } = useContext(CartContext);
  const { data: session } = useSession();
  const [cartProducts, setCartProducts] = useState([]);
  const [scroll, setScroll] = useState(0);
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    setCartProducts(cart);
  }, [cart]);

  const { wishlist } = useContext(WishlistContext);
  const [wishlistCount, setWishlistCount] = useState(0);

  useEffect(() => {
    setWishlistCount(wishlist.length);
  }, [wishlist]);

  useEffect(() => {
    const scroll = () => {
      setScroll(window.scrollY);
    };
    window.addEventListener("scroll", scroll, false);
    return () => window.removeEventListener("scroll", scroll);
  }, []);

  const activeCategory = activeIndex !== null ? categories[activeIndex] : null;
  const activeHasSubCategories = activeCategory?.subCategory?.length > 0;

  return (
    <>
      <header className="bg-white w-full shadow-sm min-h-[70px] transition duration-500 relative z-40 border-b border-brand-light">
        <Topbar />
        <div className="container mx-auto flex flex-wrap items-center justify-between py-3 lg:gap-y-4 gap-1 gap-y-2 sm:gap-y-6 gap-x-4">
          <div className="flex items-center">
            <Sidebar />
            <Link href="/" className="ml-2 lg:ml-0">
              <Image
                src="https://ichchapuron.nyc3.cdn.digitaloceanspaces.com/ichchapuron/ichchapuron-bangla-logo.png"
                alt="ichchapuron"
                width={120}
                height={100}
                className="w-24 md:w-32"
              />
            </Link>
          </div>
          <Search />
          <div className="flex items-center space-x-4 sm:space-x-8 max-md:ml-auto lg:w-auto overflow-hidden">
            <div className="relative sm:flex text-sm font-medium cursor-pointer hidden group">
              <Link href="/wishlist">
                <Heart
                  className="text-brand-navy group-hover:text-brand-sky transition-colors"
                  aria-label="heart icon for wishlist"
                />
                <span className="bg-brand-sky text-xs px-1.5 font-semibold min-w-[20px] h-5 flex items-center justify-center text-white rounded-full absolute -top-2 left-[60%] shadow-brand-sm">
                  {wishlistCount}
                </span>
              </Link>
            </div>
            <div className="relative flex text-sm cursor-pointer group">
              <Link href="/cart">
                <ShoppingCart
                  className="text-brand-navy group-hover:text-brand-sky transition-colors"
                  aria-label="shopping cart"
                />
                <span className="bg-brand-sky text-xs px-1.5 font-semibold min-w-[20px] h-5 flex items-center justify-center text-white rounded-full absolute -top-2 left-[60%] shadow-brand-sm">
                  {cartProducts?.length || 0}
                </span>
              </Link>
            </div>
            {session?.user?.email ? (
              <ProfileMenu className="" />
            ) : (
              <Link href="/login">
                <Button
                  variant="outline"
                  className="flex items-center gap-1 border-brand-sky text-brand-deep hover:bg-brand-sky hover:text-white transition-colors"
                >
                  <User size={18} />
                  Sign In
                </Button>
              </Link>
            )}
          </div>
        </div>

        <div
          className="hidden lg:flex gradient-navy items-start relative"
          onMouseLeave={() => setActiveIndex(null)}
        >
          <div className="flex justify-start w-full container mx-auto relative">
            {categories?.map((data, index) => {
              const hasSubCategories = data?.subCategory?.length > 0;
              const isActive = activeIndex === index;

              return (
                <div
                  className="relative px-4 py-3"
                  key={index}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <div
                    className={`flex items-center gap-1 cursor-pointer font-medium text-base transition-colors ${
                      isActive ? "text-brand-sky" : "text-white"
                    }`}
                  >
                    <Link href={data?.link ? data?.link : "/"}>
                      {data?.title}
                    </Link>
                    {hasSubCategories && (
                      <ChevronDown
                        size={14}
                        className={`mt-0.5 transition-transform duration-200 ${
                          isActive ? "rotate-180" : ""
                        }`}
                      />
                    )}
                  </div>
                  <span
                    className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-brand-sky transition-all duration-300 ${
                      isActive ? "w-3/4" : "w-0"
                    }`}
                  />
                </div>
              );
            })}

            <div
              className={`absolute top-full left-1/2 -translate-x-1/2 w-screen max-w-[1200px] bg-white shadow-brand-lg rounded-b-2xl border border-brand-light transition-all duration-300 ease-in-out z-50 ${
                activeHasSubCategories
                  ? "opacity-100 visible translate-y-0"
                  : "opacity-0 invisible -translate-y-1 pointer-events-none"
              }`}
            >
              {activeHasSubCategories && (
                <>
                  <div className="grid grid-cols-4 gap-6 p-8">
                    {activeCategory.subCategory.slice(0, 24).map((book, idx) => (
                      <Link
                        key={idx}
                        href={book?.path || "/"}
                        className="flex items-center gap-2 text-gray-600 hover:text-brand-deep text-sm hover:underline transition-colors group"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-sky/40 group-hover:bg-brand-sky transition-colors" />
                        {book?.name} {book?.banglaName && `(${book?.banglaName})`}
                      </Link>
                    ))}
                  </div>
                  {activeCategory.subCategory.length > 24 && (
                    <div className="flex justify-end px-8 pb-4">
                      <Link
                        href={activeCategory?.link || "/"}
                        className="text-brand-deep font-semibold hover:text-brand-sky hover:underline text-sm transition-colors"
                      >
                        See More...
                      </Link>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <header
        className={`fixed z-40 bg-white w-full h-[70px] sm:min-h-20 shadow-brand transition-all duration-500 border-b border-brand-light ${
          scroll > 150 ? "top-0 translate-y-0" : "-top-full translate-y-0"
        }`}
      >
        <div className="container mx-auto flex items-center justify-between py-3 lg:gap-y-4 gap-1 gap-y-2 sm:gap-y-6 gap-x-4 h-full">
          <div className="flex items-center">
            <Sidebar />
            <Link href="/" className="ml-2 lg:ml-0">
              <Image
                src="https://ichchapuron.nyc3.cdn.digitaloceanspaces.com/ichchapuron/ichchapuron-bangla-logo.png"
                alt="ichchapuron"
                width={100}
                height={100}
                className="w-24 md:w-32 hidden md:block"
              />
            </Link>
          </div>
          <Search />
          <div className="flex items-center sm:space-x-8 order-2">
            <div className="relative md:flex text-sm font-medium cursor-pointer hidden group">
              <Link href="/wishlist">
                <Heart
                  className="text-brand-navy group-hover:text-brand-sky transition-colors"
                  aria-label="heart icon for wishlist"
                />
                <span className="bg-brand-sky text-xs px-1.5 font-semibold min-w-[20px] h-5 flex items-center justify-center text-white rounded-full absolute -top-2 left-[60%] shadow-brand-sm">
                  {wishlistCount}
                </span>
              </Link>
            </div>
            <div className="relative flex text-sm cursor-pointer group">
              <Link href="/cart">
                <ShoppingCart
                  className="text-brand-navy group-hover:text-brand-sky transition-colors"
                  aria-label="shopping cart"
                />
                <span className="bg-brand-sky text-xs px-1.5 font-semibold min-w-[20px] h-5 flex items-center justify-center text-white rounded-full absolute -top-2 left-[60%] shadow-brand-sm">
                  {cartProducts?.length || 0}
                </span>
              </Link>
            </div>
            <div className="hidden sm:block">
              {session?.user?.email ? (
                <ProfileMenu className="" />
              ) : (
                <Link href="/login" className="hidden md:inline-block">
                  <Button
                    variant="outline"
                    className="flex items-center gap-1 border-brand-sky text-brand-deep hover:bg-brand-sky hover:text-white transition-colors"
                  >
                    <User size={18} />
                    Sign In
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Navbar;